/**
 * إعدادات النظام — موعد الإقفال وقواعد الطلب، يضبطها مدير النظام
 */
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";

interface Setting {
  key: string; value: string; labelAr: string; valueType: string; updatedAt: string;
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [draft, setDraft]     = useState<Record<string, string>>({});

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<{ data: Setting[] }>("/settings").then((r) => r.data.data),
  });

  const save = async (key: string, value: string) => {
    setError(""); setSuccess("");
    try {
      await api.put(`/settings/${key}`, { value });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["request-rules"] });
      setDraft((d) => { const n = { ...d }; delete n[key]; return n; });
      setSuccess("تم حفظ الإعداد");
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ"
      );
    }
  };

  const input = (s: Setting) => {
    const val = draft[s.key] ?? s.value;
    const set = (v: string) => setDraft((d) => ({ ...d, [s.key]: v }));

    if (s.valueType === "bool") {
      return (
        <select className="input" value={val} onChange={(e) => set(e.target.value)}>
          <option value="true">نعم</option>
          <option value="false">لا</option>
        </select>
      );
    }
    return (
      <input className="input" dir="ltr"
        type={s.valueType === "time" ? "time" : s.valueType === "int" ? "number" : "text"}
        min={s.valueType === "int" ? 0 : undefined}
        value={val} onChange={(e) => set(e.target.value)} />
    );
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="page-header">
        <h1 className="page-title">إعدادات النظام</h1>
        <p className="page-subtitle">قواعد استلام الطلبات وموعد الإقفال اليومي</p>
      </div>

      {error   && <div className="alert-error   mb-4">{error}</div>}
      {success && <div className="alert-success mb-4">{success}</div>}
      {isLoading && <p className="text-sm text-gray-400">جاري التحميل...</p>}

      <div className="space-y-3">
        {settings.map((s) => {
          const dirty = draft[s.key] !== undefined && draft[s.key] !== s.value;
          return (
            <div key={s.key} className="card flex items-end gap-3">
              <div className="flex-1">
                <label className="label">{s.labelAr}</label>
                {input(s)}
                <p className="text-[10px] text-gray-400 mt-1" dir="ltr">{s.key}</p>
              </div>
              <button className="btn-primary" disabled={!dirty}
                onClick={() => save(s.key, draft[s.key]!)}>
                حفظ
              </button>
            </div>
          );
        })}
      </div>

      <div className="card mt-4 bg-gray-50 text-sm text-gray-600">
        <strong className="block mb-1">كيف تعمل هذه القواعد معاً</strong>
        طلبات يوم معيّن تُقبل حتى الساعة المحددة في «موعد إقفال استلام الطلبات» من اليوم
        السابق بعدد «الأيام المسبقة». مثال: أيام مسبقة = 1 وموعد الإقفال = 12:00 ⇒
        طلبات الغد تُقبل حتى ظهر اليوم. مدير النظام وحده يستطيع التسجيل بعد الموعد،
        ويُعلَّم السجل كتجاوز إداري في سجل التدقيق.
      </div>
    </div>
  );
}
